import React from 'react'
import PropTypes from 'prop-types'
import { observer, inject } from 'mobx-react'

import { APP_CONFIG_GET_A_NEWS_LETTER_ORIGIN } from '@config/app.config'
import { SAVE_ERROR_KINDS } from '@client/utils/saveError'
import { allowLeaving } from '@client/utils/pageUnload'

import './styles.scss'

const MESSAGE_KEYS = {
  [SAVE_ERROR_KINDS.VALIDATION]: 'saveError.validation',
  [SAVE_ERROR_KINDS.AUTORESPONDER_RUNNING]: 'saveError.autoresponderRunning',
  [SAVE_ERROR_KINDS.PLAN]: 'saveError.plan',
  [SAVE_ERROR_KINDS.GONE]: 'saveError.gone',
  [SAVE_ERROR_KINDS.OTHER]: 'saveError.other',
}

// Where the user can fix what the editor can't. Validation errors are fixed in
// the editor itself, so they get no link.
const LINKS = {
  [SAVE_ERROR_KINDS.AUTORESPONDER_RUNNING]: {
    href: `${APP_CONFIG_GET_A_NEWS_LETTER_ORIGIN}/mails/autoresponders`,
    key: 'saveError.autorespondersLink',
  },
  [SAVE_ERROR_KINDS.PLAN]: {
    href: `${APP_CONFIG_GET_A_NEWS_LETTER_ORIGIN}/upgrade?planLevel=20`,
    key: 'saveError.upgradeLink',
  },
  // The edits can never be saved, and the banner says so: no leave prompt on
  // top (FE-1454). The other links keep it, since fixing the cause there could
  // still let the edits be saved.
  [SAVE_ERROR_KINDS.GONE]: {
    href: `${APP_CONFIG_GET_A_NEWS_LETTER_ORIGIN}`,
    key: 'saveError.dashboardLink',
    leavesOnPurpose: true,
  },
}

/**
 * Tells the user that the backend rejected a save, and why (FE-1431).
 *
 * A banner rather than a dialog: for a validation error the user has to keep
 * editing to fix it. The backend's own messages are shown for validation and
 * unknown errors; the other kinds have a known cause with its own copy.
 *
 * Rendered conditionally rather than toggled with a class, so `role="alert"`
 * announces it when it appears, as in RemoteChangeWarning.
 */
export const SaveErrorBanner = ({ app, editor }) => {
  const { saveError } = editor
  if (!saveError || saveError.dismissed) {
    return null
  }

  const showDetails =
    saveError.kind === SAVE_ERROR_KINDS.VALIDATION ||
    saveError.kind === SAVE_ERROR_KINDS.OTHER
  const details = showDetails ? saveError.messages.join('; ') : ''
  const link = LINKS[saveError.kind]

  return (
    <div className="save-error-banner" role="alert">
      <div className="save-error-banner--message" title={details || undefined}>
        {app.formatMessage(MESSAGE_KEYS[saveError.kind])}
        <If condition={details}>
          <span className="save-error-banner--details"> {details}</span>
        </If>
      </div>
      <div className="save-error-banner--actions">
        <If condition={link}>
          <a
            href={link.href}
            onClick={link.leavesOnPurpose ? allowLeaving : undefined}
            className="save-error-banner--btn bp6-button bp6-intent-danger"
          >
            {app.formatMessage(link.key)}
          </a>
        </If>
        <button
          type="button"
          onClick={editor.dismissSaveError}
          className="save-error-banner--btn bp6-button bp6-intent-danger bp6-icon-cross"
        >
          {app.formatMessage('saveError.dismiss')}
        </button>
      </div>
    </div>
  )
}

SaveErrorBanner.propTypes = {
  app: PropTypes.object.isRequired,
  editor: PropTypes.object.isRequired,
}

export default inject('app', 'editor')(observer(SaveErrorBanner))
